import { useMemo } from 'react'
import { useCurrentUser } from '@appic/lib/auth/UserContext'
import { useServiceLayerAuth } from '@appic/lib/auth/ServiceLayerAuthProvider'
import { usePermissionMap } from './usePermissionMap'
import type { PermissionAction } from '@appic/data'
import { hasPermission, hasAppAccess } from '@appic/data'

interface UsePermissionsProps {
  userRole?: string
}

/**
 * Main permissions hook — delegates to usePermissionMap (ADR-033).
 *
 * Keeps backward-compatible API: canAccess, canWrite, canAdmin, canPerform,
 * canAccessRoute, visibilityScope, and the flat `permissions` object.
 */
export const usePermissions = ({ userRole: propUserRole }: UsePermissionsProps = {}) => {
  const { role: contextRole, impersonating } = useCurrentUser()
  const { role: domainRole } = useServiceLayerAuth()
  const userRole = propUserRole || contextRole || 'Viewer'
  const isSuperadmin = impersonating ? contextRole === 'Admin' : domainRole === 'superadmin'

  const { permissionMap, isLoading, error: _error } = usePermissionMap()

  // Whether we have a real permission map from the API
  const isLegacyMode = permissionMap.length === 0 && !isLoading

  const canAccess = (section: string, action?: PermissionAction, featureId?: string): boolean => {
    if (isSuperadmin) return true
    if (action && featureId) return hasPermission(permissionMap, section, action, featureId)
    if (action) return hasPermission(permissionMap, section, action)
    if (permissionMap.length > 0) return hasAppAccess(permissionMap, section)
    return false
  }

  const canWrite = (section: string, featureId?: string): boolean => {
    if (isSuperadmin) return true
    return hasPermission(permissionMap, section, 'write', featureId)
  }

  const canAdmin = (section: string, featureId?: string): boolean => {
    if (isSuperadmin) return true
    return hasPermission(permissionMap, section, 'admin', featureId)
  }

  const canPerform = (action: string, resource: string): boolean => {
    if (isSuperadmin) return true
    const actionMap: Record<string, PermissionAction> = {
      view: 'read', read: 'read',
      create: 'write', edit: 'write', write: 'write',
      delete: 'delete',
      admin: 'admin', manage: 'admin',
      execute: 'execute',
    }
    const perm = actionMap[action] ?? 'read'
    return hasPermission(permissionMap, resource, perm)
  }

  const getAccessLevel = (section: string) => {
    if (isSuperadmin) return 'admin' as const
    if (hasPermission(permissionMap, section, 'admin')) return 'admin' as const
    if (hasPermission(permissionMap, section, 'write')) return 'write' as const
    if (hasPermission(permissionMap, section, 'read')) return 'read' as const
    return 'none' as const
  }

  const visibilityScope = (resource: string): 'own' | 'team' | 'all' => {
    if (isSuperadmin) return 'all'
    if (hasPermission(permissionMap, resource, 'admin')) return 'all'
    if (hasPermission(permissionMap, resource, 'delete')) return 'team'
    if (hasPermission(permissionMap, resource, 'write')) return 'team'
    return 'own'
  }

  const canAccessRoute = (route: string): boolean => {
    if (isSuperadmin) return true
    const routeToApp: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/analytics': 'analytics',
      '/sales': 'sales',
      '/purchase': 'purchase',
      '/inventory': 'inventory',
      '/warehouse': 'warehouse',
      '/accounting': 'accounting',
      '/crm': 'crm',
      '/ai': 'ai',
      '/workflows': 'workflows',
      '/kanban': 'kanban',
      '/helpdesk': 'helpdesk',
      '/learning': 'learning',
      '/settings': 'settings',
      '/scout': 'scout',
      '/po-sprints': 'po-sprints',
      '/wiki': 'wiki',
    }
    const appId = routeToApp[route] || Object.entries(routeToApp).find(
      ([prefix]) => route.startsWith(prefix + '/')
    )?.[1]
    if (!appId) return true
    return canAccess(appId)
  }

  // Legacy compatibility: individual permission flags
  const permissions = useMemo(() => {
    const check = (section: string) => isSuperadmin || hasAppAccess(permissionMap, section)
    const checkWrite = (section: string) => isSuperadmin || hasPermission(permissionMap, section, 'write')
    const checkAdmin = (section: string) => isSuperadmin || hasPermission(permissionMap, section, 'admin')
    return {
      canViewDashboard: check('dashboard'),
      canViewSales: check('sales'),
      canCreateSales: checkWrite('sales'),
      canEditSales: checkWrite('sales'),
      canDeleteSales: checkAdmin('sales'),
      canViewPurchase: check('purchase'),
      canCreatePurchase: checkWrite('purchase'),
      canEditPurchase: checkWrite('purchase'),
      canDeletePurchase: checkAdmin('purchase'),
      canViewInventory: check('inventory'),
      canCreateInventory: checkWrite('inventory'),
      canEditInventory: checkWrite('inventory'),
      canDeleteInventory: checkAdmin('inventory'),
      canViewWarehouse: check('warehouse'),
      canOperateWarehouse: checkWrite('warehouse'),
      canViewAccounting: check('accounting'),
      canManageAccounting: checkAdmin('accounting'),
      canViewCRM: check('crm'),
      canManageCRM: checkWrite('crm'),
      canUseAI: check('ai'),
      canViewHelpdesk: check('helpdesk'),
      canManageHelpdesk: checkWrite('helpdesk'),
      canViewSettings: check('settings'),
      canManageSettings: checkAdmin('settings'),
      canManageUsers: checkAdmin('settings'),
      canViewAudit: check('settings'),
    }
  }, [permissionMap, isSuperadmin])

  return {
    ...permissions,
    canAccess,
    canAccessRoute,
    canPerform,
    canWrite,
    canAdmin,
    getAccessLevel,
    visibilityScope,
    userRole,
    isSuperadmin,
    isLegacyMode,
    permissionMap,
  }
}
